import { apiGatewayClient } from '@/common/apis/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// قرارداد عین سوییچ «مراجعین من» در نلسون‌فرانت (services/bookListApi.ts، ۲۰۲۶-۱۰-۱۲).

const ONLINE_VISIT_SERVICE_TYPE_ID = 8;

export interface UserCenterService {
  can_booking?: number | string;
  service_type_id?: number | string;
  [key: string]: unknown;
}

const unwrapItems = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (obj.data && typeof obj.data === 'object') {
      const inner = obj.data as Record<string, unknown>;
      if (Array.isArray(inner.items)) return inner.items as T[];
      if (Array.isArray(inner.data)) return inner.data as T[];
    }
    if (Array.isArray(obj.data)) return obj.data as T[];
  }
  return [];
};

/**
 * مسیر جنرال لوتولی — مسیر قدیمی /v1/user-center-services روی گیت‌وی حذف شده (۴۰۴)
 * و سوییچ همیشه «غیرفعال» دیده می‌شد.
 */
export const getUserCenterServices = async (userCenterId: string): Promise<UserCenterService[]> => {
  const { data } = await apiGatewayClient.get('/v1/luthuli/user-center-services', {
    params: { user_center_id: userCenterId, server_id: 1 },
  });
  return unwrapItems<UserCenterService>(data);
};

/** وضعیت on/off از ردیف service_type_id=8 — نه هر ردیفی که روی همان user_center هست */
export const getOnlineVisitCanBooking = (services: UserCenterService[] | null | undefined): number => {
  const online = services?.find(row => Number(row?.service_type_id) === ONLINE_VISIT_SERVICE_TYPE_ID);
  return Number(online?.can_booking ?? 0);
};

export const useOnlineVisitStatus = (userCenterId?: string) =>
  useQuery(
    ['doctorHome', 'onlineVisitServices', userCenterId],
    async () => getOnlineVisitCanBooking(await getUserCenterServices(userCenterId!)),
    {
      enabled: !!userCenterId,
      staleTime: 60 * 1000,
    },
  );

/** تعداد نوبت‌های آینده — قبل از غیرفعال‌سازی مسیر قدیمی (برای پرسش لغو نوبت‌ها) */
export const getOnlineVisitCountAfterDeactivation = async (userCenterId: string): Promise<number> => {
  const { data: res } = await apiGatewayClient.get('/v1/Onlinevisit-doctor-appointments-after-deactivation-time', {
    params: { user_center_id: userCenterId },
  });
  const data = res?.data ?? res ?? {};
  return Number(data.count_id ?? data.count ?? 0);
};

/** این سرویس سوییچ است: وضعیت «فعلی» را می‌گیرد و برعکسش می‌کند؛ read-back سرور ملاک است */
export const toggleCanBooking = async (userCenterId: string, currentCanBooking: number): Promise<number> => {
  const { data } = await apiGatewayClient.patch('/v1/canbookingon&off', {
    can_booking: String(currentCanBooking),
    user_center_id: userCenterId,
  });
  const rows = unwrapItems<{ can_booking?: number | string }>(data);
  return Number(rows[0]?.can_booking ?? 0);
};

export const deleteBooksAfterDeactiveOnlineVisit = async (userCenterId: string): Promise<unknown> => {
  const { data } = await apiGatewayClient.patch('/v1/delete-book-after-deactive-online-visit', {
    can_booking: '0',
    user_center_id: userCenterId,
  });
  return data;
};

// --- ov-booking-guard (پیایلوت: فقط پزشکانِ فلگ‌خورده — بک‌اند: لوتولی) ---
// قرارداد عین سوییچ «مراجعین من» در نلسون‌فرانت (services/bookListApi.ts).

const LUTHULI_PROXY_BASE = '/v1/luthuli';

export type OnlineVisitGuardChat = {
  ticket_id: number;
  book_id?: string | null;
  patient_name?: string | null;
  hours_overdue?: number;
  chat_url?: string;
};

export type OnlineVisitGuardResponse = {
  allowed?: boolean;
  open_count?: number;
  chats?: OnlineVisitGuardChat[];
  future_count?: number;
  can_booking?: number | string;
  verified?: boolean;
  /** دورهٔ پلکان جریمه (ISO): با allowed:false و chats:[] — «تا … غیرفعال است» */
  locked_until?: string | null;
};

/** فعال‌سازی با گارد: چت بازِ بی‌پاسخ داشت → allowed:false + جدول چت‌ها (هیچ تغییری نمی‌کند) */
export const enableOnlineVisitGuarded = async (userCenterId: string): Promise<OnlineVisitGuardResponse> => {
  const { data } = await apiGatewayClient.post(
    `${LUTHULI_PROXY_BASE}/online-visit-booking/user-centers/${userCenterId}/enable`,
  );
  return data ?? {};
};

/** غیرفعال‌سازی با شمارش نوبت‌های آینده (future_count برای مودال لغو) */
export const disableOnlineVisitGuarded = async (userCenterId: string): Promise<OnlineVisitGuardResponse> => {
  const { data } = await apiGatewayClient.post(
    `${LUTHULI_PROXY_BASE}/online-visit-booking/user-centers/${userCenterId}/disable`,
  );
  return data ?? {};
};

/** لغو نوبت‌های آینده — همان قرارداد قدیمی از مسیر لوتولی */
export const deleteBooksAfterDeactiveOnlineVisitGuarded = async (userCenterId: string): Promise<unknown> => {
  const { data } = await apiGatewayClient.patch(`${LUTHULI_PROXY_BASE}/delete-book-after-deactive-online-visit`, {
    can_booking: '0',
    user_center_id: userCenterId,
  });
  return data;
};

/** بعد از تغییر، read-back سرور را مستقیم در کش وضعیت بگذار (بدون رفت‌وبرگشت دوباره) */
export const useSetOnlineVisitStatus = () => {
  const queryClient = useQueryClient();

  return (userCenterId: string, canBooking: number) =>
    queryClient.setQueryData(['doctorHome', 'onlineVisitServices', userCenterId], canBooking);
};
